/**
 * @file        src/modals/AuthModal.jsx
 * @description Two-step OTP sign-in modal, opened by the single nav
 *              "Log in / Create account" button. A segmented switch at the top
 *              picks the account type — Personal account or Corporate login — and
 *              below it step 1 collects an email address, step 2 a 6-digit OTP with
 *              auto-advance, backspace backtrack, paste-to-fill and a 30-second
 *              resend timer. Mirrors the source's `sendOtp` / `otpInput` / `otpKey`
 *              / `otpPaste` / `verifyOtp` / `resendOtp` / `startOtpTimer` helpers
 *              (~line 2664 in the design HTML).
 *
 *              EMAIL ONLY, BY DESIGN. There is no phone / SMS-OTP path here and
 *              none should be added: `/generateloginotp` + `/AuthenticateLogin` are
 *              keyed on the email address, and `components/PhoneInput.jsx` (used in
 *              the booking + profile forms) is deliberately not wired into auth.
 *
 *              The account-type switch is a LABELLED ENTRY POINT, not a server-side
 *              gate: both tabs hit the same two endpoints, and the real role
 *              ('b2c_user' / 'b2b_owner' / 'b2b_user') comes back on the user record
 *              and is what `doLogin` stores. Corporate simply adds the copy and the
 *              "Register your company" route that had no reachable trigger before.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-10-09 Claude — Send OTP now WAITS for /generateloginotp. It used to show
 *                          "OTP sent" and open the code step before the answer, so a
 *                          failed send (wrong SMTP password) read as sent and then
 *                          raised an error over the code screen. A failure now keeps
 *                          the customer on the email step with the server's message;
 *                          the button reads "Sending…" meanwhile. A failed Resend no
 *                          longer starts the 30-second timer.
 *   - 2026-09-24 Claude — Send OTP cannot fire twice: sendOtp now sets `busy` for the
 *                          request's lifetime (the button was already wired to it and
 *                          never engaged), so a stalled page pressed twice — the live
 *                          report, two codes in the inbox — sends once. Resend OTP now
 *                          actually calls /generateloginotp (it only restarted the
 *                          timer before), under the same guard, and is disabled while
 *                          a send is in flight. The server de-duplicates too.
 *   - 2026-09-16 Claude — Both password inputs (the corporate login and the
 *                          reset step’s new password) are now <PasswordField>,
 *                          which adds a reveal toggle. A mistyped password was
 *                          invisible until the server rejected it, which reads
 *                          the same as a wrong account — and on the reset step a
 *                          typo becomes the password, locking the company out of
 *                          the account it just recovered. Markup for the row moved
 *                          into the shared component unchanged; no auth call,
 *                          field name or ref behaviour differs.
 *   - 2026-09-12 Claude — Clicking "Forgot password?" no longer relabels the Log in
 *                          button "Logging in…": the single `busy` boolean drove
 *                          every button's caption, so a reset-code request read as
 *                          a sign-in attempt. `busy` is now derived from
 *                          `busyAction` ('login' / 'reset' / 'save'), which is what
 *                          the captions read; every setBusy() call site is unchanged.
 *   - 2026-09-10 Claude — A failed OTP send is no longer swallowed. /generateloginotp
 *                          returns 502 when the brand has no usable SMTP config
 *                          (email_settings row / CREDENTIAL_ENCRYPTION_KEY); the
 *                          silent catch here meant the customer landed on
 *                          the OTP screen with no code and no explanation. The error
 *                          now shows in the existing toast; we still advance to the
 *                          OTP step so Resend stays reachable.
 *   - 2026-08-18 Claude — The close (×) button is visible again. It sits OUTSIDE the
 *                          card by design, but the panel had gained
 *                          `max-h-[88svh] overflow-y-auto` for its tallest steps, and a
 *                          scroll container clips both axes (CSS computes the other axis
 *                          from `visible` to `auto` as soon as one is not `visible`) —
 *                          so the button rendered outside the box and was clipped to
 *                          nothing. Neon's identical markup still showed it because its
 *                          panel has no overflow. The card is now wrapped in a plain
 *                          `relative w-full max-w-md` div that owns the positioning and
 *                          does NOT scroll; the inner panel keeps the height cap. Escape
 *                          and the backdrop already closed the modal, so this restores
 *                          the only affordance that was actually missing.
 *   - 2026-08-17 Claude — MOBILE: the dialog had NO height cap and no scroll, so on a
 *                          short viewport (landscape phone, or portrait with the
 *                          on-screen keyboard up) its last row — the action buttons —
 *                          was simply off screen with no way to reach it. Now capped at
 *                          88svh and scrollable. `svh`, not `vh`: a phone browser's
 *                          `100vh` is the toolbars-hidden height, so a vh cap still
 *                          overshoots what is visible. Desktop is unaffected.
 *   - 2026-08-04 Claude — "Forgot password?" on the corporate password step runs an
 *                          in-modal reset: /passwordReset-otp issues a code, then
 *                          /AuthResetPassword sets the new password and we sign in
 *                          with it (that route returns a message, never a token). It
 *                          replaces the "sign in with an emailed OTP" fallback and is
 *                          the only route to a password for companies registered
 *                          before the signup form collected one.
 *   - 2026-08-04 Claude — Corporate now signs in with a PASSWORD: "Next" moves to a
 *                          password screen that calls /auth/login (the backend's one
 *                          bcrypt-verifying route, returning the same
 *                          { user: { …, token } } envelope as the OTP path, so
 *                          nothing downstream changes). Personal is unchanged.
 *                          CorporateRegisterModal now REQUIRES a password — without
 *                          one /corporate-register stores a random hash and the owner
 *                          it just created could never pass this screen.
 *                          A "Sign in with an emailed OTP" fallback stays on the
 *                          password step for accounts registered before that.
 *   - 2026-08-04 Claude — Close button moved OUTSIDE the card; icon and heading share
 *                          one row; dropped the "We'll email you a one-time password"
 *                          line and its shield; both tabs are now the SAME height (a
 *                          two-line floor on the sub, and a fixed-height slot for the
 *                          register link that renders empty on personal).
 *   - 2026-08-04 Claude — Added the Personal account / Corporate login switch and
 *                          the corporate copy + "New company? Register here" link,
 *                          which opens CorporateRegisterModal. That modal was fully
 *                          built but UNREACHABLE — its only trigger lived in
 *                          layout/Sidebar.jsx, which nothing has rendered since the
 *                          TopNav fork, so there was no way into the corporate flow
 *                          from the shipped app at all.
 *   - 2026-07-29 Claude — "Verify & continue" (step 2) recoloured from the teal→mint
 *                          gradient to the same black (#1C1C1C) as "Send OTP", so
 *                          both steps of the modal match the rest of the site.
 *   - 2026-07-23 Claude — "Send OTP" button recoloured from the teal→mint gradient
 *                          to black (#1C1C1C) per client request.
 *   - 2026-07-20 Claude — Privacy link repointed to the unified /legal/privacy; the
 *                          `mode` subscription that chose a per-product legal page
 *                          is gone.
 *   - 2026-07-19 Claude — Terms is now ONE document for all products — the footer
 *                          link points at /legal/terms (privacy still varies by
 *                          funnel).
 *   - 2026-06-22 Claude — Initial port. Verify triggers
 *                         `useBooking.getState().doLogin(email)` then closes
 *                         the modal — the store rehydrates `loggedIn`,
 *                         `userEmail`, `walletBalance` and runs any pending
 *                         action queued via `requireLogin()`.
 *   - 2026-07-09 Claude — The footer "Terms" / "Privacy Statement" were inert
 *                         <span>s (styled clickable, did nothing). They are now
 *                         real <a> links to /legal/{flight|hotel}-terms and
 *                         -privacy, chosen from the booking `mode`, opened in a
 *                         new tab so the modal + booking funnel survive.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PasswordField from '../components/PasswordField.jsx';
import { useBooking } from '../store/booking.store.js';
import { post, setAccessToken } from '../api/client.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Matches /corporate-register's own floor, so the reset form cannot set a password
// the signup form would have rejected.
const MIN_PASSWORD = 8;
// How long "OTP sent" stays visible under the button before the code step
// replaces it — long enough to read, far shorter than the email send it used
// to wait on.
const OTP_SENT_BEAT_MS = 450;

// Step ids. 1 and 2 are the original email → OTP pair; PASSWORD is the corporate
// second step, reached from step 1 by "Next" instead of "Send OTP"; RESET is the
// forgot-password form reached from PASSWORD.
const STEP_EMAIL = 1;
const STEP_OTP = 2;
const STEP_PASSWORD = 3;
const STEP_RESET = 4;

// The two account types offered by the switch. Personal signs in with an emailed
// OTP; corporate signs in with a password against /auth/login.
const ACCOUNTS = {
  personal: {
    label: 'Personal account',
    title: 'Log in with email OTP',
    sub: 'Log in to manage and book your trips with Beyond Destination.',
    placeholder: 'you@example.com',
  },
  corporate: {
    label: 'Corporate login',
    title: 'Corporate log in',
    // Kept close in LENGTH to the personal sub (64 chars vs 60), not just in tone:
    // the two wrap to the same number of lines at every width, which is half of
    // what keeps the modal from resizing when the tab is switched.
    sub: 'Log in to your company account — shared wallet and team bookings.',
    placeholder: 'you@company.com',
  },
};

export default function AuthModal({ open, onClose, payload }) {
  // The legal links no longer follow the funnel: Terms and Privacy are each ONE
  // document covering every product, so the `mode` subscription that picked a
  // per-product page is gone with them.
  const [account, setAccount] = useState('personal');
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Forgot-password form: the emailed 6-digit code and the replacement password.
  const [resetOtp, setResetOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // WHICH action is in flight, not merely whether one is. A single boolean
  // disabled every button AND drove every label, so "Forgot password?" — which
  // also runs through setBusy — turned the Log in button into "Logging in…"
  // while the only thing happening was a reset code being emailed.
  // `setBusy` keeps its old signature (every call site below is unchanged) and
  // takes an optional action name; setBusy(false) clears both.
  const [busyAction, setBusyAction] = useState('');
  const busy = busyAction !== '';
  const setBusy = (on, action = 'busy') => setBusyAction(on ? action : '');
  const [error, setError] = useState('');
  const inputsRef = useRef([]);
  const passwordRef = useRef(null);

  // Auto-dismiss the validation toast.
  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 4000);
    return () => clearTimeout(t);
  }, [error]);
  const tickRef = useRef(null);
  // True from the Send OTP click until the customer returns to the email step:
  // disables the button and shows the "OTP sent" line under it.
  const [otpSent, setOtpSent] = useState(false);
  const advanceRef = useRef(null);

  // body lock + Esc
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // reset state every time the modal opens
  useEffect(() => {
    if (open) {
      // A caller can land straight on the corporate tab with
      // nh:open-auth { payload: { account: 'corporate' } }. Anything else = personal.
      setAccount(payload?.account === 'corporate' ? 'corporate' : 'personal');
      setStep(STEP_EMAIL);
      setPassword('');
      setResetOtp('');
      setNewPassword('');
      setOtp(['', '', '', '', '', '']);
      setSecondsLeft(0);
      setError('');
      setBusy(false);
      setOtpSent(false);
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    }
    return () => {
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      clearTimeout(advanceRef.current);
    };
  }, [open]);

  const startTimer = () => {
    setSecondsLeft(30);
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(tickRef.current);
          tickRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  // Ask the backend to email a real OTP and WAIT for its answer. That emailed
  // code is the only one accepted, so "OTP sent" and the code step may appear
  // only once the server says the mail went out. A failed send (e.g. the brand's
  // SMTP password is wrong) keeps the customer on the email step with the
  // server's message, and Send OTP stays pressable for a retry.
  const sendOtp = async () => {
    const e = (email || '').trim();
    setError('');
    if (!EMAIL_RE.test(e)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (otpSent || busy) return;
    setEmail(e);
    setBusy(true, 'otp');
    try {
      await post('/generateloginotp', { email: e });
    } catch (err) {
      setError(err?.response?.data?.message
        || 'We could not email your code. Please try again in a moment.');
      return;
    } finally {
      setBusy(false);
    }
    setOtpSent(true);
    clearTimeout(advanceRef.current);
    advanceRef.current = setTimeout(() => {
      setStep(STEP_OTP);
      setOtp(['', '', '', '', '', '']);
      startTimer();
      setTimeout(() => inputsRef.current[0]?.focus(), 0);
    }, OTP_SENT_BEAT_MS);
  };

  // Corporate "Next": no network call — just validate the address and move to the
  // password screen. Nothing is emailed, which is the point of the corporate path.
  const goPassword = () => {
    const e = (email || '').trim();
    setError('');
    if (!EMAIL_RE.test(e)) {
      setError('Please enter a valid email address.');
      return;
    }
    setEmail(e);
    setPassword('');
    setStep(STEP_PASSWORD);
    setTimeout(() => passwordRef.current?.focus(), 0);
  };

  // Corporate sign-in proper. /auth/login is the backend's one password-verifying
  // route (bcrypt.compare) and it returns the SAME { user: { …, token } } envelope
  // as the OTP path, so everything downstream — token, role, business_id, wallet —
  // is identical to an OTP sign-in.
  const submitPassword = async () => {
    if (busy) return;
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    setBusy(true, 'login');
    setError('');
    try {
      const res = await post('/auth/login', { email, password });
      const token = res?.user?.token;
      if (!token) throw new Error('No token');
      setAccessToken(token);
      useBooking.getState().doLogin(email, res?.user);
      onClose?.();
    } catch (err) {
      // The server distinguishes bad credentials (401) from a deactivated employee
      // or suspended company (403) — surface its message rather than a generic one.
      setError(err?.response?.data?.message || 'Invalid email or password.');
      setPassword('');
      passwordRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  // "Forgot password?" — asks the server to issue a reset code for the address
  // already on screen, then swaps to the reset form. /passwordReset-otp 404s on an
  // unknown address, which is why its message is surfaced rather than swallowed.
  const requestReset = async () => {
    if (busy) return;
    const e = (email || '').trim();
    if (!EMAIL_RE.test(e)) {
      setError('Please enter a valid email address.');
      return;
    }
    setBusy(true, 'reset');
    setError('');
    try {
      await post('/passwordReset-otp', { email: e });
      setResetOtp('');
      setNewPassword('');
      setStep(STEP_RESET);
    } catch (err) {
      setError(err?.response?.data?.message || 'We could not start a password reset for that address.');
    } finally {
      setBusy(false);
    }
  };

  // Set the new password against the emailed code, then sign in with it.
  // /AuthResetPassword answers with a message only — it never returns a token — so
  // the sign-in is a second call rather than something we can read off the reset.
  const submitReset = async () => {
    if (busy) return;
    if (!/^\d{6}$/.test(resetOtp)) {
      setError('Enter the 6-digit code from your email.');
      return;
    }
    if (newPassword.length < MIN_PASSWORD) {
      setError(`Password must be at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true, 'save');
    setError('');
    try {
      await post('/AuthResetPassword', { email, otp: resetOtp, password: newPassword });
      const res = await post('/auth/login', { email, password: newPassword });
      const token = res?.user?.token;
      if (!token) throw new Error('No token');
      setAccessToken(token);
      useBooking.getState().doLogin(email, res?.user);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'That code is invalid or has expired.');
    } finally {
      setBusy(false);
    }
  };

  const otpBack = () => {
    setStep(STEP_EMAIL);
    setOtpSent(false);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    setSecondsLeft(0);
  };

  const completeVerify = async (digits) => {
    const code = digits.join('');
    if (code.length !== 6 || busy) return;
    setBusy(true);
    setError('');
    try {
      // Real backend login → returns { user: { ..., token } }. Store the token so
      // authed calls (favourites, bookings) work, then mirror the demo store state.
      const res = await post('/AuthenticateLogin', { email, otp: code });
      const token = res?.user?.token;
      if (!token) throw new Error('No token');
      setAccessToken(token);
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      // Pass the backend user through: it carries role/business_id, so a B2B
      // agent signing in with OTP is recognised as an agent, not a B2C user.
      useBooking.getState().doLogin(email, res?.user);
      onClose?.();
    } catch (err) {
      setError(err?.response?.data?.message || 'Invalid or expired OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      inputsRef.current[0]?.focus();
    } finally {
      setBusy(false);
    }
  };

  const otpInput = (i, raw) => {
    const v = (raw || '').replace(/\D/g, '').slice(0, 1);
    const next = [...otp];
    next[i] = v;
    setOtp(next);
    if (v && i < 5) inputsRef.current[i + 1]?.focus();
    if (v && i === 5) completeVerify(next);
  };

  const otpKey = (e, i) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) {
      const prev = inputsRef.current[i - 1];
      if (prev) {
        prev.focus();
        const next = [...otp];
        next[i - 1] = '';
        setOtp(next);
      }
    }
  };

  const otpPaste = (e) => {
    const t = (e.clipboardData || window.clipboardData).getData('text').replace(/\D/g, '').slice(0, 6);
    if (!t) return;
    e.preventDefault();
    const next = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) next[i] = t[i] || '';
    setOtp(next);
    const lastIdx = Math.min(t.length, 6) - 1;
    inputsRef.current[lastIdx]?.focus();
    if (t.length >= 6) completeVerify(next);
  };

  const verifyOtp = () => completeVerify(otp);

  // Switching account type rewinds to step 1 and clears the email: a code already
  // sent belongs to the address it was sent to, and carrying a personal address
  // into the corporate tab is the one way this switch could mislead.
  const selectAccount = (next) => {
    if (next === account) return;
    setAccount(next);
    setStep(STEP_EMAIL);
    setEmail('');
    setPassword('');
    setResetOtp('');
    setNewPassword('');
    setOtp(['', '', '', '', '', '']);
    setError('');
    setSecondsLeft(0);
    setOtpSent(false);
    clearTimeout(advanceRef.current);
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  };

  // Hand off to the company self-registration modal. Closing first is what lets
  // ModalsHost swap: it renders one modal at a time, keyed by the open name.
  const openCorporateRegister = () => {
    onClose?.();
    window.dispatchEvent(new CustomEvent('nh:open-modal', { detail: { name: 'corporateRegister' } }));
  };

  // Resend asks the server for a code. It used to clear the boxes and restart the
  // 30-second timer WITHOUT calling the backend, so "Resend OTP" sent nothing and
  // the only way to a fresh code was back to the email step — which is where the
  // double press came from. Same in-flight guard as sendOtp; a resend inside the
  // server's cooldown is answered "already sent" and mails nothing.
  const resendOtp = async () => {
    if (busy || secondsLeft > 0) return;
    setError('');
    setBusy(true, 'otp');
    try {
      await post('/generateloginotp', { email: (email || '').trim() });
    } catch (err) {
      // No timer on a failed send: nothing was mailed, so the customer may
      // retry straight away.
      setError(err?.response?.data?.message
        || 'We could not email your code. Please try again in a moment.');
      return;
    } finally {
      setBusy(false);
    }
    setOtp(['', '', '', '', '', '']);
    inputsRef.current[0]?.focus();
    startTimer();
  };

  if (!open) return null;

  const heading = step === STEP_PASSWORD ? 'Enter your password'
    : step === STEP_RESET ? 'Reset your password'
      : ACCOUNTS[account].title;
  const subline = step === STEP_PASSWORD ? 'Your company account is protected by a password, not a one-time code.'
    : step === STEP_RESET ? 'Enter the 6-digit code we emailed you, then choose a new password.'
      : ACCOUNTS[account].sub;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      {error && createPortal(
        <div className="fixed top-6 right-6 z-[100] flex items-center gap-3 px-4 py-3 rounded-2xl border border-warn/40 bg-panel shadow-2xl max-w-[92vw] animate-[fade_.25s_ease]">
          <span className="w-9 h-9 rounded-xl bg-warn/15 text-warnink flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="M12 2 1 21h22zm0 5 7.5 13h-15zM11 10v5h2v-5zm0 6v2h2v-2z" /></svg>
          </span>
          <span className="text-sm font-medium text-ink">{error}</span>
          <button
            type="button"
            onClick={() => setError('')}
            className="ml-1 w-7 h-7 rounded-lg text-muted hover:text-ink hover:bg-ink/5 flex items-center justify-center shrink-0 cursor-pointer"
            aria-label="Dismiss"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
          </button>
        </div>,
        document.body,
      )}
      <div onClick={onClose} className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      {/* The close button is positioned OUTSIDE this card (-top-12 / sm:-right-12),
          so it cannot live inside the scrolling panel below: `overflow-y-auto` makes
          that panel a scroll container, and a scroll container clips BOTH axes — CSS
          computes the other axis from `visible` to `auto` the moment one axis is not
          `visible` — so the button was painted outside the box and clipped away.
          That is why Beyond had no × while Neon (whose panel has no overflow and
          otherwise identical markup) shows one. This wrapper restores the positioning
          context WITHOUT a height cap or overflow, so the button escapes the card
          again while the panel keeps the scrolling it needs for its tallest steps. */}
      <div className="relative w-full max-w-md">
        {/* Close sits OUTSIDE the card, so it never crowds the content or forces the
            tab switch to leave a gap for it. Two positions, because 48px of clear
            space beside the panel only exists once the viewport is wider than the
            panel: to the RIGHT of the card from `sm` up (where the panel is 448px in
            a ≥640px viewport, leaving 96px each side), and ABOVE it below that,
            where horizontal room runs out but vertical room does not. */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-0 -top-12 sm:top-0 sm:-right-12 w-9 h-9 rounded-full bg-panel border border-line flex items-center justify-center text-muted hover:text-ink hover:border-neon cursor-pointer shadow-card"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5"><path fill="currentColor" d="m12 10.6 4.9-4.9 1.4 1.4-4.9 4.9 4.9 4.9-1.4 1.4-4.9-4.9-4.9 4.9-1.4-1.4 4.9-4.9-4.9-4.9 1.4-1.4z" /></svg>
        </button>
        {/* max-h + scroll: this panel had NO height cap. The tallest step (create
            account, or reset-password with the code + two password fields) is taller
            than a landscape phone and taller than a portrait one once the on-screen
            keyboard is up, and the parent centres it with no scroll — so the submit
            button was simply cut off with no way to reach it. `svh` (small viewport
            height), not `vh`: on a mobile browser `100vh` is the height with the
            toolbars HIDDEN, so a vh cap still overshoots the area you can see. */}
        <div className="relative w-full max-h-[88svh] overflow-y-auto no-scrollbar rounded-3xl border border-line bg-panel p-6">
          {/* Account-type switch. Only on step 1 — once a code is out, the choice is
              already baked into the address it was sent to, and a live segment there
              would invite a mid-flow switch that silently voids the OTP. Step 2's
              "Change" link comes back here.
              Full width again now that the close button lives outside the card — it
              used to need `mr-11` to keep the corporate tab from running under the X.
              The active pill carries the standard Jet Ink recipe, so it inverts to
              cream under [data-theme='dark'] with every other primary button in the
              app — see "The Jet Ink rule" in styles/global.css. */}
          {step === STEP_EMAIL && (
            <div className="flex p-1 mb-5 rounded-full bg-night border border-line" role="tablist">
              {Object.entries(ACCOUNTS).map(([key, a]) => (
                <button
                  key={key}
                  role="tab"
                  aria-selected={account === key}
                  onClick={() => selectAccount(key)}
                  className={
                    'flex-1 py-2.5 rounded-full text-[13px] font-bold transition cursor-pointer ' +
                    (account === key ? 'bg-[#1C1C1C] text-white' : 'text-muted hover:text-ink')
                  }
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}

          {/* Icon and heading share ONE row. Stacked, the 48px badge pushed the title
              onto its own line and the block ate a third of the modal before any
              field appeared. `min-w-0` lets the heading shrink rather than force the
              row wider than the panel. */}
          <div className="flex items-center gap-3">
            <span className="w-12 h-12 shrink-0 rounded-full bg-neon flex items-center justify-center">
              {account === 'corporate' ? (
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-onaccent"><path fill="currentColor" d="M10 4h4a2 2 0 0 1 2 2v2h4a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2h4V6a2 2 0 0 1 2-2m0 4h4V6h-4z" /></svg>
              ) : (
                <svg viewBox="0 0 24 24" className="w-6 h-6 text-onaccent"><path fill="currentColor" d="M21 16v-2l-8-5V3.5C13 2.67 12.33 2 11.5 2S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z" /></svg>
              )}
            </span>
            <h2 className="font-display text-2xl font-extrabold leading-tight min-w-0">
              {heading}
            </h2>
          </div>
          {/* min-h-10 = two lines at text-sm's 20px leading. The personal sub is one
              line on a wide panel and the corporate one is two, so without a floor the
              whole modal jumped 20px on every tab switch. */}
          <p className="text-muted text-sm mt-2 min-h-10">{subline}</p>

          {step === STEP_EMAIL && (
            <div className="mt-5">
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted mb-1.5">
                {account === 'corporate' ? 'Work email address' : 'Email address'}
              </label>
              <div className="field bg-night/70 rounded-xl border border-line px-4 py-2.5 flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-muted shrink-0"><path fill="currentColor" d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2m0 4-8 5-8-5V6l8 5 8-5z" /></svg>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') (account === 'corporate' ? goPassword() : sendOtp()); }}
                  placeholder={ACCOUNTS[account].placeholder}
                  className="w-full bg-transparent outline-none text-[15px] placeholder:text-muted/50"
                />
              </div>
              {/* Corporate goes to a password screen (no email is sent), personal to
                  the emailed code — hence the two labels for one button. */}
              <button
                onClick={account === 'corporate' ? goPassword : sendOtp}
                disabled={busy || otpSent}
                className="w-full mt-4 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold hover:brightness-125 cursor-pointer transition disabled:opacity-60"
              >
                {account === 'corporate' ? 'Next' : (busyAction === 'otp' ? 'Sending…' : 'Send OTP')}
              </button>
              {/* The only live way into company self-registration. The row is ALWAYS
                  rendered at a fixed height and merely emptied for personal —
                  conditionally mounting it made the modal 30px taller on the corporate
                  tab, so it grew and shrank as the customer compared the two. */}
              <div className="mt-3 h-[18px] leading-[18px] text-[12px] text-muted text-center">
                {account === 'personal' && otpSent && (
                  <span className="inline-flex items-center gap-1 text-neon font-semibold" role="status">
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5"><path fill="currentColor" d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" /></svg>
                    OTP sent
                  </span>
                )}
                {account === 'corporate' && (
                  <>
                    New company?{' '}
                    <button onClick={openCorporateRegister} className="text-neon font-semibold cursor-pointer hover:text-ink">Register here</button>
                  </>
                )}
              </div>
            </div>
          )}

          {step === STEP_PASSWORD && (
            <div className="mt-5">
              <p className="text-sm text-muted">
                Logging in as <span className="text-ink font-semibold">{email}</span>{' '}
                <button onClick={() => { setStep(STEP_EMAIL); setPassword(''); }} className="text-neon font-semibold cursor-pointer hover:text-ink">Change</button>
              </p>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted mt-4 mb-1.5">Password</label>
              <PasswordField
                ref={passwordRef}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') submitPassword(); }}
                autoComplete="current-password"
                placeholder="Your account password"
              />
              <button onClick={submitPassword} disabled={busy} className="w-full mt-4 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold hover:brightness-125 cursor-pointer transition disabled:opacity-60">{busyAction === 'login' ? 'Logging in…' : 'Log in'}</button>
              {/* This is also the way IN for every company registered before the signup
                  form collected a password: the server gave those accounts a random
                  hash, so resetting is the only route to a password they know. */}
              <p className="text-[11px] text-muted mt-3 text-center">
                <button onClick={requestReset} disabled={busy} className="text-neon font-semibold cursor-pointer hover:text-ink disabled:opacity-60">{busyAction === 'reset' ? 'Sending code…' : 'Forgot password?'}</button>
              </p>
            </div>
          )}

          {step === STEP_RESET && (
            <div className="mt-5">
              <p className="text-sm text-muted">
                Resetting for <span className="text-ink font-semibold">{email}</span>{' '}
                <button onClick={() => setStep(STEP_PASSWORD)} className="text-neon font-semibold cursor-pointer hover:text-ink">Back</button>
              </p>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted mt-4 mb-1.5">6-digit code</label>
              <div className="field bg-night/70 rounded-xl border border-line px-4 py-2.5 flex items-center gap-2">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-muted shrink-0"><path fill="currentColor" d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2m0 4-8 5-8-5V6l8 5 8-5z" /></svg>
                <input
                  value={resetOtp}
                  onChange={(e) => { setResetOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
                  inputMode="numeric"
                  maxLength={6}
                  autoComplete="one-time-code"
                  placeholder="123456"
                  className="w-full bg-transparent outline-none text-[15px] tracking-[0.3em] placeholder:tracking-normal placeholder:text-muted/50"
                />
              </div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-muted mt-4 mb-1.5">New password</label>
              <PasswordField
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') submitReset(); }}
                autoComplete="new-password"
                minLength={MIN_PASSWORD}
                placeholder={`At least ${MIN_PASSWORD} characters`}
              />
              <button onClick={submitReset} disabled={busy} className="w-full mt-4 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold hover:brightness-125 cursor-pointer transition disabled:opacity-60">{busyAction === 'save' ? 'Saving…' : 'Save & log in'}</button>
              <p className="text-[11px] text-muted mt-3 text-center">
                Didn't get the code?{' '}
                <button onClick={requestReset} disabled={busy} className="text-neon font-semibold cursor-pointer hover:text-ink disabled:opacity-60">{busyAction === 'reset' ? 'Sending…' : 'Send it again'}</button>
              </p>
            </div>
          )}

          {step === STEP_OTP && (
            <div className="mt-5">
              <p className="text-sm text-muted">
                Enter the 6-digit code sent to <span className="text-ink font-semibold">{email}</span>{' '}
                <button onClick={otpBack} className="text-neon font-semibold cursor-pointer hover:text-ink">Change</button>
              </p>
              <div className="flex justify-between gap-2 mt-4" onPaste={otpPaste}>
                {otp.map((v, i) => (
                  <input
                    key={i}
                    ref={(el) => (inputsRef.current[i] = el)}
                    value={v}
                    onChange={(e) => otpInput(i, e.target.value)}
                    onKeyDown={(e) => otpKey(e, i)}
                    inputMode="numeric"
                    maxLength={1}
                    // Flexible width (was fixed w-11): six 44px boxes + gaps needed
                    // ~304px and overflowed the ~280px modal on a 360px phone.
                    className="flex-1 min-w-0 h-12 text-center text-xl font-bold bg-night/70 rounded-xl border border-line outline-none focus:border-neon transition"
                  />
                ))}
              </div>
              <button onClick={verifyOtp} disabled={busy} className="w-full mt-4 py-3 rounded-xl bg-[#1C1C1C] text-white font-bold hover:brightness-125 cursor-pointer transition disabled:opacity-60">{busy ? 'Verifying…' : 'Verify & continue'}</button>
              <p className="text-[11px] text-muted mt-3 text-center">
                Didn't get the code?{' '}
                {secondsLeft > 0
                  ? <span> Resend in {secondsLeft}s</span>
                  : <button onClick={resendOtp} disabled={busy} className="text-neon font-semibold cursor-pointer hover:text-ink disabled:opacity-60">{busyAction === 'otp' ? 'Sending…' : 'Resend OTP'}</button>}
              </p>
            </div>
          )}

          {/* Real links (were inert <span>s that looked clickable but did nothing).
              Opened in a new tab so the sign-in modal and the in-progress booking
              funnel behind it are never lost. */}
          <p className="text-[11px] text-muted mt-5 text-center">
            By continuing you agree to the Beyond Destination{' '}
            <a href="/legal/terms" target="_blank" rel="noreferrer" className="text-neon cursor-pointer hover:underline">Terms</a>
            {' '}&amp;{' '}
            <a href="/legal/privacy" target="_blank" rel="noreferrer" className="text-neon cursor-pointer hover:underline">Privacy Statement</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
